import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";

export const btn = "btn-pill inline-flex items-center justify-center gap-1.5 border border-line text-ink px-3.5 py-2 text-xs font-semibold hover:border-silver transition-colors disabled:opacity-40 disabled:pointer-events-none";
export const btnPrimary = "btn-pill inline-flex items-center justify-center gap-2 bg-action text-action-ink hover:bg-action-hover px-5 py-2.5 text-sm font-semibold transition-colors disabled:opacity-40 disabled:pointer-events-none";
export const btnBrand = "btn-pill inline-flex items-center justify-center gap-2 bg-brand text-brand-ink hover:bg-brand-hover px-5 py-2.5 text-sm font-semibold transition-colors disabled:opacity-40 disabled:pointer-events-none";
export const selectCls = "bg-surface border border-line text-ink rounded-lg px-3 py-2.5 text-sm outline-none focus:border-brand focus:ring-1 focus:ring-brand transition-colors disabled:opacity-60";
export const fieldCls = "w-full bg-surface border border-line text-ink placeholder:text-faint rounded-lg px-3 py-2.5 text-sm outline-none focus:border-brand focus:ring-1 focus:ring-brand transition-colors disabled:opacity-60 disabled:bg-elevated";
export const labelCls = "text-[10px] uppercase tracking-[0.2em] text-faint font-semibold";

const TONES = {
  muted: "bg-elevated text-dim border-line",
  red: "bg-brand/10 text-red-700 border-brand/40",
  green: "bg-emerald-500/10 text-emerald-700 border-emerald-500/30",
  amber: "bg-amber-500/10 text-amber-700 border-amber-500/30",
  dark: "bg-action text-action-ink border-action",
};

export const Badge = ({ children, tone = "muted", testId, title }) => (
  <span data-testid={testId} title={title}
    className={`inline-flex items-center gap-1 text-[10px] font-semibold px-2.5 py-0.5 rounded-full border whitespace-nowrap ${TONES[tone]}`}>{children}</span>
);

export const STATUS_TONE = { leer: "muted", offen: "amber", vorbereitet: "dark", fehlgeschlagen: "red", uebermittelt: "green", nachlieferung: "amber" };
export const STATUS_DOT = { leer: "bg-line", offen: "bg-amber-500", vorbereitet: "bg-ink", fehlgeschlagen: "bg-brand", uebermittelt: "bg-emerald-500", nachlieferung: "bg-brand" };

export const Spinner = ({ className = "w-7 h-7" }) => (
  <div className={`${className} border-2 border-line border-t-brand rounded-full animate-spin`} />
);

export const Field = ({ label, children, className = "" }) => (
  <label className={`block ${className}`}>
    <span className="block text-xs text-dim font-medium mb-1.5">{label}</span>
    {children}
  </label>
);

export const docsLabel = (n) => `${n} ${n === 1 ? "Dokument" : "Dokumente"}`;
export const fmtBytes = (n) => (n >= 1048576 ? `${(n / 1048576).toFixed(1).replace(".", ",")} MB` : `${Math.max(1, Math.round((n || 0) / 1024))} KB`);
export const fmtDate = (iso) => (iso ? new Date(iso.length === 10 ? `${iso}T12:00:00` : iso).toLocaleDateString("de-AT", { day: "2-digit", month: "2-digit", year: "numeric" }) : "–");
export const fmtDateTime = (iso) => (iso ? new Date(iso).toLocaleString("de-AT", { dateStyle: "short", timeStyle: "short" }) : "–");
export const fmtEUR = (v) => (v == null ? "–" : v.toLocaleString("de-AT", { style: "currency", currency: "EUR" }));

export const errMsg = (e, fallback) => {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => String(x.msg || "").replace(/^Value error, /, "")).join(", ");
  return fallback;
};

export const blobErr = async (e, fallback) => {
  const data = e?.response?.data;
  if (data instanceof Blob) {
    try { return JSON.parse(await data.text()).detail || fallback; } catch { return fallback; }
  }
  return errMsg(e, fallback);
};

export const useAuthBlob = (url) => {
  const [state, setState] = useState({ src: null, failed: false });
  useEffect(() => {
    if (!url) return undefined;
    let alive = true;
    let obj = null;
    setState({ src: null, failed: false });
    api.get(url, { responseType: "blob" })
      .then(({ data }) => { if (alive) { obj = URL.createObjectURL(data); setState({ src: obj, failed: false }); } })
      .catch(() => alive && setState({ src: null, failed: true }));
    return () => { alive = false; if (obj) URL.revokeObjectURL(obj); };
  }, [url]);
  return state;
};

export const saveBlob = (data, filename) => {
  const obj = URL.createObjectURL(data);
  const a = document.createElement("a");
  a.href = obj;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(obj), 4000);
};

export const downloadAuthed = async (url, filename) => {
  const { data } = await api.get(url, { responseType: "blob" });
  saveBlob(data, filename);
};
